import { useState, useEffect } from "react";
import { FaSearch, FaBars, FaTimes } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import MegaMenu from "./MegaMenu";
import API from "../api/api";
import SearchBox from "./SearchBox";
import DarkToggle from "./DarkToggle";

export default function NavMenu() {
    const [activeMenu, setActiveMenu] = useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
    const [categoryArticles, setCategoryArticles] = useState({});
    const location = useLocation();

    /* ---------------- SCREEN SIZE ---------------- */
    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    /* ---------------- CLOSE MENU ON ROUTE CHANGE ---------------- */
    useEffect(() => {
        setActiveMenu(null);
        setMobileOpen(false);
    }, [location.pathname]);

    /* ---------------- MENU LABELS ---------------- */
    const MENU = {
        home: "Home",
        programming: "Programming",
        ai: "AI",
        devops: "DevOps & Cloud",
        cybersecurity: "Cybersecurity",
        technology: "Technology",
        science: "Science",
        startup_business: "Startup Business",
    };

    const menuKeys = Object.keys(MENU);

    /* ---------------- FETCH ARTICLES FROM API ---------------- */
    useEffect(() => {
        API
            .get("/articles/articlelist")
            .then((res) => {
                // ✅ Normalize API response
                const articles = Array.isArray(res.data)
                    ? res.data
                    : res.data?.data || [];

                const grouped = {};

                articles.forEach((article) => {
                    if (!article.category) return;

                    const key = article.category
                        .toLowerCase()
                        .replace(/\s+/g, "_");

                    if (!grouped[key]) grouped[key] = [];
                    grouped[key].push(article);
                });

                setCategoryArticles(grouped);
            })
            .catch((err) => {
                console.error("Error fetching navbar articles:", err);
            });
    }, []);

    return (
        <div
            className="sticky top-0 z-50 bg-white border-b shadow-md px-6 py-3"
            onMouseLeave={() => setActiveMenu(null)}
        >
            <div className="flex justify-between items-center lg:justify-center relative">

                {/* MOBILE TOGGLE */}
                <button
                    className="lg:hidden text-2xl"
                    aria-label="Open navigation menu"
                    onClick={() => setMobileOpen(!mobileOpen)}
                >
                    {mobileOpen ? <FaTimes /> : <FaBars />}
                </button>

                {/* DESKTOP MENU */}
                <ul className="hidden lg:flex space-x-8 font-semibold text-gray-800">
                    {menuKeys.map((key) =>
                        key === "home" ? (
                            <li key={key}>
                                <Link
                                    to="/"
                                    className="px-1 cursor-pointer hover:text-indigo-600 transition"
                                >
                                    {MENU[key]}
                                </Link>
                            </li>
                        ) : (
                            <li
                                key={key}
                                onMouseEnter={() => setActiveMenu(key)}
                                className={`relative cursor-pointer px-1 transition
                  ${activeMenu === key ? "text-indigo-600 font-bold" : ""}
                  hover:text-indigo-600`}
                            >
                                {MENU[key]}
                                <span
                                    className={`absolute left-0 -bottom-1 h-[2px] bg-indigo-600 transition-all duration-300
                    ${activeMenu === key ? "w-full" : "w-0"}`}
                                />
                            </li>
                        )
                    )}
                </ul>

                <div className="hidden md:flex items-center gap-4">
                    <div className="w-[280px]">
                        <SearchBox />
                    </div>
                    <DarkToggle />
                </div>
            </div>

            {/* MOBILE DROPDOWN */}
            {!isDesktop && mobileOpen && (
                <div className="bg-gray-100 mt-3 rounded-lg shadow-inner p-4 space-y-3">
                    {menuKeys.map((key) =>
                        key === "home" ? (
                            <Link
                                key={key}
                                to="/"
                                className="block text-base font-medium p-2 bg-white rounded border hover:bg-gray-50"
                                onClick={() => setMobileOpen(false)}
                            >
                                {MENU[key]}
                            </Link>
                        ) : (
                            <div
                                key={key}
                                className="text-base font-medium p-2 bg-white rounded cursor-pointer border hover:bg-gray-50"
                                onClick={() => {
                                    setActiveMenu(key);
                                    setMobileOpen(false);
                                }}
                            >
                                {MENU[key]}
                            </div>
                        )
                    )}
                </div>
            )}

            {/* MEGA MENUS (DB DATA) */}
            {isDesktop &&
                menuKeys
                    .filter((key) => key !== "home")
                    .map((key) => (
                        <MegaMenu
                            key={key}
                            category={MENU[key]}
                            items={(categoryArticles[key] || []).slice(0, 8)}
                            visible={activeMenu === key}
                        />
                    ))}
        </div>
    );
}
